import { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import FormRow from '../components/FormRow';
import { useAuth } from '../context/AuthContext';
import {
  listUsersApi, createUserApi, updateUserApi, setUserStatusApi, setUserPasswordApi, sendWelcomeEmailApi
} from '../api/http';
import { FaPen, FaSyncAlt, FaTrash, FaKey, FaLaptopCode, FaUserShield, FaPlus } from 'react-icons/fa';

// Estilos modernos alineados con index.css
const btnPrimary = {
  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
  border: '1px solid #111827',
  color: '#ffffff',
  fontWeight: 600,
  transition: 'all 0.15s ease',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  padding: '8px 12px'
};

const btnSecondary = {
  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
  border: '1px solid #d1d5db',
  color: '#374151',
  fontWeight: 500,
  transition: 'all 0.15s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  padding: '8px 12px'
};

/* ========= Helpers ========= */
const onlyDigits = (s) => (s || '').replace(/\D/g, '');

/* RUT */
function rutDV(num) {
  let m = 2, s = 0;
  for (let i = num.length - 1; i >= 0; i--) { s += Number(num[i]) * m; m = m === 7 ? 2 : m + 1; }
  const r = 11 - (s % 11);
  return r === 11 ? '0' : r === 10 ? 'K' : String(r);
}
function normalizeRut(raw) {
  const clean = (raw || '').replace(/\./g, '').replace(/-/g, '').toUpperCase();
  if (clean.length < 2) return clean;
  const body = String(parseInt(clean.slice(0, -1), 10));
  const dv = clean.slice(-1);
  return `${body}-${dv}`;
}
function formatRut(raw) {
  const norm = normalizeRut(raw);
  const [body = '', dv = ''] = norm.split('-');
  const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return dv ? `${withDots}-${dv}` : withDots;
}
function isValidRut(raw) {
  const norm = normalizeRut(raw);
  if (!/^\d{1,8}-[\dK]$/.test(norm)) return false;
  const [num, dv] = norm.split('-');
  return rutDV(num) === dv.toUpperCase();
}

/* Email / Fono CL / Password */
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
function digitsPhoneCL(raw) {
  let d = onlyDigits(raw);
  if (d.startsWith('56')) d = d.slice(2);
  if (d.startsWith('0')) d = d.slice(1);
  if (d.startsWith('9')) d = d.slice(1);
  return d.slice(0, 8);
}
function formatPhoneCL(raw) {
  const d = digitsPhoneCL(raw);
  const left = d.slice(0, 4);
  const right = d.slice(4);
  return `+56 9 ${left}${right ? ' ' + right : ''}`.trimEnd();
}
function normalizePhoneCL(raw) {
  const d = digitsPhoneCL(raw);
  return d.length === 8 ? `569${d}` : null;
}
const isValidEmail = (e) => emailRe.test(e || '');
const isValidPhone = (p) => {
  if (!p) return true;
  const norm = normalizePhoneCL(p);
  return !!norm;
};
const isValidPassword = (p) => typeof p === 'string' && p.trim().length >= 8;

function validate(form, isEditing) {
  const errs = {};
  if (!form.name?.trim()) errs.name = 'Requerido';
  if (!isValidRut(form.rut)) errs.rut = 'RUT inválido';
  if (!isValidEmail(form.email)) errs.email = 'Email inválido';
  if (!isValidPhone(form.phone)) errs.phone = 'Fono inválido (+56 9 xxxx xxxx)';
  if (!isEditing && !isValidPassword(form.password)) errs.password = 'Mínimo 8 caracteres';
  return errs;
}

/* ========= Pills ========= */
function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  const label = { active: 'Activo', inactive: 'Inactivo', deleted: 'Eliminado' }[s] || status;
  const base = { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 };
  const color = s === 'inactive' ? { background: '#f59e0b', color: '#111' }
    : s === 'deleted' ? { background: '#ef4444', color: '#fff' }
      : { background: '#10b981', color: '#fff' };
  return <span style={{ ...base, ...color }}>{label}</span>;
}
function RolePill({ role }) {
  const r = (role || '').toLowerCase();
  const base = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 };
  let style = { background: '#1f2937', color: '#fff' };
  let Icon = FaLaptopCode, label = 'Programador';
  if (r === 'admin') { style = { background: '#3b82f6', color: '#fff' }; Icon = FaUserShield; label = 'Admin'; }
  return <span style={{ ...base, ...style }}><Icon />{label}</span>;
}

/* ========= Modales ========= */
function PasswordModal({ open, onClose, onConfirm, userName, userEmail }) {
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState('');
  useEffect(() => { if (open) { setPw(''); setPw2(''); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;

  const fieldStyle = { width: '100%', minWidth: 0 };
  return (
    <div role="dialog" aria-modal="true"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 12 }}
      tabIndex={-1}
    >
      <div style={{ background: '#f3f4f6', color: '#111', padding: 18, borderRadius: 12, width: 'min(96vw, 560px)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>Cambiar contraseña</h4>
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>

        {/* Información del usuario */}
        {(userName || userEmail) && (
          <div style={{
            background: '#e0f2fe',
            border: '1px solid #bae6fd',
            borderRadius: 8,
            padding: '12px',
            marginBottom: 16
          }}>
            {userName && (
              <div style={{ fontWeight: 600, color: '#0c4a6e', marginBottom: 4 }}>
                {userName}
              </div>
            )}
            {userEmail && (
              <div style={{ fontSize: '14px', color: '#075985' }}>
                {userEmail}
              </div>
            )}
          </div>
        )}

        <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); onConfirm(pw, pw2); }}>
          {/* Honeypot para prevenir autofill */}
          <input type="text" name="fake-username" autoComplete="username" tabIndex={-1}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />

          <FormRow label="Nueva contraseña">
            <input
              type="password"
              style={fieldStyle}
              placeholder="Min 8 caracteres"
              value={pw}
              onChange={e => setPw(e.target.value)}
              name="new-password-change"
              autoComplete="new-password"
              spellCheck={false}
            />
          </FormRow>
          <FormRow label="Confirmar contraseña">
            <input
              type="password"
              style={fieldStyle}
              value={pw2}
              onChange={e => setPw2(e.target.value)}
              name="confirm-password-change"
              autoComplete="new-password"
              spellCheck={false}
            />
          </FormRow>
        </form>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => onConfirm(pw, pw2)}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function UserFormModal({
  open, title, editingId,
  form, touched, errors, serverErrs,
  setField, markTouched,
  loading, onClose, onSubmit
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;

  const fieldStyle = { width: '100%', minWidth: 0 };
  const isEditing = !!editingId;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 12 }}
      tabIndex={-1}
    >
      <div style={{ background: '#f3f4f6', color: '#111', padding: 18, borderRadius: 12, width: 'min(96vw, 560px)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          autoComplete={isEditing ? 'on' : 'off'}
          key={isEditing ? `edit-${editingId}` : 'create'}
        >
          {/* Honeypots contra autofill */}
          <input type="text" name="username" autoComplete="username" tabIndex={-1}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
          <input type="password" name="current-password" autoComplete="current-password" tabIndex={-1}
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />

          <FormRow label="Nombre">
            <input
              autoFocus
              style={fieldStyle}
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              onBlur={() => markTouched('name')}
              required
              name={isEditing ? 'edit-name' : 'new-name'}
              autoComplete="off"
              autoCapitalize="words"
              spellCheck={false}
            />
            {touched.name && (errors.name || serverErrs.name) && <small className="error">{errors.name || serverErrs.name}</small>}
          </FormRow>

          <FormRow label="RUT">
            <input
              style={fieldStyle}
              value={form.rut}
              onChange={e => setField('rut', e.target.value)}
              onBlur={() => markTouched('rut')}
              placeholder="12.345.678-5"
              maxLength={12}
              required
              name={isEditing ? 'edit-rut' : 'new-rut'}
              autoComplete="off"
              inputMode="numeric"
            />
            {touched.rut && (errors.rut || serverErrs.rut) && <small className="error">{errors.rut || serverErrs.rut}</small>}
          </FormRow>

          <FormRow label="Email">
            <input
              type="email"
              style={fieldStyle}
              value={form.email}
              onChange={e => setField('email', e.target.value)}
              onBlur={() => markTouched('email')}
              required
              name={isEditing ? 'edit-email' : 'new-email'}
              autoComplete="off"
              spellCheck={false}
            />
            {touched.email && (errors.email || serverErrs.email) && <small className="error">{errors.email || serverErrs.email}</small>}
          </FormRow>

          <FormRow label="Fono">
            <input
              style={fieldStyle}
              value={form.phone}
              onChange={e => setField('phone', e.target.value)}
              onBlur={() => markTouched('phone')}
              placeholder="+56 9 1234 5678"
              maxLength={16}
              name={isEditing ? 'edit-phone' : 'new-phone'}
              autoComplete="off"
              inputMode="tel"
            />
            {touched.phone && (errors.phone || serverErrs.phone) && <small className="error">{errors.phone || serverErrs.phone}</small>}
          </FormRow>

          {!isEditing && (
            <FormRow label="Password (temporal)">
              <input
                type="password"
                style={fieldStyle}
                value={form.password}
                onChange={e => setField('password', e.target.value)}
                onBlur={() => markTouched('password')}
                required
                name="new-password"
                autoComplete="new-password"
                spellCheck={false}
              />
              {touched.password && (errors.password || serverErrs.password) && <small className="error">{errors.password || serverErrs.password}</small>}
            </FormRow>
          )}

          <FormRow label="Rol">
            <select
              style={fieldStyle}
              value={form.role}
              onChange={e => setField('role', e.target.value)}
              name={isEditing ? 'edit-role' : 'new-role'}
              autoComplete="off"
            >
              <option value="programmer">Programador</option>
              <option value="admin">Admin (también programador)</option>
            </select>
          </FormRow>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn" disabled={loading}>
              {loading ? 'Guardando…' : isEditing ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


/* ========= Banner de feedback ========= */
function Banner({ kind = 'info', children, onClose }) {
  if (!children) return null;
  const styles = kind === 'error'
    ? { bg: '#fee2e2', fg: '#991b1b', br: '#fecaca' }
    : { bg: '#dcfce7', fg: '#065f46', br: '#bbf7d0' };
  return (
    <div style={{ background: styles.bg, color: styles.fg, border: `1px solid ${styles.br}`, padding: 10, borderRadius: 8, marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ overflowWrap: 'anywhere' }}>{children}</div>
      <button className="btn" onClick={onClose}>OK</button>
    </div>
  );
}

/* ========= Tabla de Usuarios con paginación ========= */
function UsersTable({ rows, canEdit, canDeactivate, canDelete, onEdit, onToggleStatus, onDelete, onChangePassword, page, pageSize, totalCount, onPageChange }) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalCount);

  // Botones de acción reutilizables
  const renderActions = (r) => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
      <button className="btn" style={btnSecondary} title="Editar" onClick={() => onEdit(r)}>
        <FaPen /><span>Editar</span>
      </button>
      <button
        className="btn"
        style={btnSecondary}
        title={r.status === 'active' ? 'Desactivar' : 'Activar'}
        onClick={() => canDeactivate(r) && onToggleStatus(r)}
        disabled={!canDeactivate(r)}
      >
        <FaSyncAlt /><span>{r.status === 'active' ? 'Desact.' : 'Activar'}</span>
      </button>
      <button
        className="btn"
        style={btnSecondary}
        title="Eliminar"
        onClick={() => canDelete(r) && onDelete(r)}
        disabled={!canDelete(r)}
      >
        <FaTrash />
      </button>
      <button className="btn" style={btnSecondary} title="Cambiar contraseña" onClick={() => onChangePassword(r)}>
        <FaKey />
      </button>
    </div>
  );

  const Pagination = () => (
    totalPages > 1 && (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '14px', color: '#64748b' }}>
          Mostrando {startIdx + 1}-{endIdx} de {totalCount} usuarios
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn"
            style={{
              ...btnSecondary,
              opacity: page === 1 ? 0.5 : 1,
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
            onClick={() => page > 1 && onPageChange(page - 1)}
            disabled={page === 1}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 500, padding: '0 8px' }}>
            Página {page} de {totalPages}
          </span>
          <button
            className="btn"
            style={{
              ...btnSecondary,
              opacity: page === totalPages ? 0.5 : 1,
              cursor: page === totalPages ? 'not-allowed' : 'pointer'
            }}
            onClick={() => page < totalPages && onPageChange(page + 1)}
            disabled={page === totalPages}
          >
            Siguiente →
          </button>
        </div>
      </div>
    )
  );

  return (
    <>
      {/* VISTA MÓVIL: CARDS */}
      <div className="mobile-only">
        {rows.map(r => (
          <div key={r.id} className="user-card" style={{ opacity: r.status === 'inactive' ? 0.7 : 1 }}>
            <div className="user-card-header">
              <div className="user-card-title">{r.name}</div>
              <StatusPill status={r.status} />
            </div>
            <div className="user-card-body">
              <div className="user-card-row">
                <span className="user-card-label">Email</span>
                <span className="user-card-value">{r.email}</span>
              </div>
              <div className="user-card-row">
                <span className="user-card-label">RUT</span>
                <span className="user-card-value">{r.rut}</span>
              </div>
              <div className="user-card-row">
                <span className="user-card-label">Rol</span>
                <span className="user-card-value"><RolePill role={r.role} /></span>
              </div>
            </div>
            <div className="user-card-actions">
              {renderActions(r)}
            </div>
          </div>
        ))}
        {!rows.length && <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>Sin usuarios</div>}

        <div style={{ marginTop: '16px' }}>
          <Pagination />
        </div>
      </div>

      {/* VISTA DESKTOP: TABLA */}
      <div className="data-table-container desktop-only">
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="col-date" style={{ width: '60px', textAlign: 'center' }}>ID</th>
                <th className="col-project" style={{ width: '22%' }}>Nombre</th>
                <th className="col-member" style={{ width: '24%' }}>Email</th>
                <th className="col-hours" style={{ width: '12%' }}>RUT</th>
                <th className="col-rate" style={{ width: '12%', textAlign: 'center' }}>Rol</th>
                <th className="col-rate" style={{ width: '10%', textAlign: 'center' }}>Estado</th>
                <th className="col-actions" style={{ width: '20%', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ opacity: r.status === 'inactive' ? 0.7 : 1 }}>
                  <td className="col-date" style={{ textAlign: 'center', fontWeight: 600 }}>{r.id}</td>
                  <td className="col-project" style={{ fontWeight: 600, color: '#1e293b' }}>{r.name}</td>
                  <td className="col-member" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }} title={r.email}>
                    {r.email}
                  </td>
                  <td className="col-hours">{r.rut}</td>
                  <td className="col-rate" style={{ textAlign: 'center' }}>
                    <RolePill role={r.role} />
                  </td>
                  <td className="col-rate" style={{ textAlign: 'center' }}>
                    <StatusPill status={r.status} />
                  </td>
                  <td className="col-actions">
                    {renderActions(r)}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>Sin usuarios</td></tr>}
            </tbody>
          </table>

          <Pagination />
        </div>
      </div>
    </>
  );
}

/* ========= Página ========= */
export default function Users() {
  const { token, user, hasRole } = useAuth();
  //admin universal, no eliminable
  const ROOT_ADMIN_ID = 1;

  // UI + datos
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // form modal
  const [form, setForm] = useState({ name: '', rut: '', email: '', phone: '', password: '', role: 'programmer' });
  const [touched, setTouched] = useState({});
  const [serverErrs, setServerErrs] = useState({});
  const [dirty, setDirty] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);

  // password modal
  const [passModal, setPassModal] = useState({ open: false, userId: null, userName: '', userEmail: '' });

  // feedback
  const [banner, setBanner] = useState({ kind: 'info', msg: '' });

  const isAdmin = hasRole ? hasRole('admin') : user?.role === 'admin';
  const canEdit = (row) => isAdmin || row.id === user?.id;
  const canDeactivate = (row) => isAdmin && row.id !== user?.id && row.status !== 'deleted' && row.id !== ROOT_ADMIN_ID;
  const canDelete = (row) => isAdmin && row.role !== 'admin' && row.id !== user?.id && row.status !== 'deleted' && row.id !== ROOT_ADMIN_ID;
  const canChangePass = (_row) => isAdmin;

  const refresh = async () => {
    try {
      const data = await listUsersApi(token);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setBanner({ kind: 'error', msg: err?.payload?.error || err?.message || 'No se pudo cargar usuarios' });
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  /* ---- Filtro + Orden ---- */
  const baseRows = useMemo(() => {
    return (rows || []).filter(r => {
      const st = (r?.status ?? '').toString().toLowerCase().trim();
      const del = r?.deleted_at != null;
      return st !== 'deleted' && !del;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return baseRows;
    return baseRows.filter(r => {
      const hay = [r.id?.toString(), r.name, r.email, r.rut, r.phone, r.role, r.status]
        .map(x => (x ?? '').toString().toLowerCase());
      return hay.some(v => v.includes(s));
    });
  }, [baseRows, q]);

  const visibleRowsSorted = useMemo(() => {
    const rank = { admin: 0, programmer: 1 };
    return [...filteredRows].sort((a, b) => {
      const ra = rank[(a.role || '').toLowerCase()] ?? 9;
      const rb = rank[(b.role || '').toLowerCase()] ?? 9;
      return (ra - rb) || (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
    });
  }, [filteredRows]);

  // resetear página cuando cambien los filtros
  useEffect(() => {
    setPage(1);
  }, [q]);

  // datos paginados
  const paginatedData = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return visibleRowsSorted.slice(startIdx, startIdx + pageSize);
  }, [visibleRowsSorted, page, pageSize]);

  /* ---- Form helpers ---- */
  const setField = (k, v) => {
    if (k === 'rut') v = formatRut(v);
    if (k === 'phone') v = formatPhoneCL(v);
    setForm(s => ({ ...s, [k]: v }));
    setDirty(d => ({ ...d, [k]: true }));
    setServerErrs(s => ({ ...s, [k]: undefined }));
  };
  const markTouched = (k) => setTouched(t => ({ ...t, [k]: true }));
  const errors = useMemo(() => validate(form, !!editingId), [form, editingId]);

  /* ---- Manejo de API genérico ---- */
  const handleApi = async (fn, successMsg) => {
    try {
      setLoading(true);
      await fn();
      if (successMsg) setBanner({ kind: 'info', msg: successMsg });
      await refresh();
    } catch (err) {
      // 409 se maneja por campo en submit(); aquí otros errores
      const msg = err?.payload?.error || err?.message || 'Error inesperado';
      setBanner({ kind: 'error', msg });
    } finally {
      setLoading(false);
    }
  };

  /* ---- CRUD ---- */
  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', rut: '', email: '', phone: '', password: '', role: 'programmer' });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(true);
  };

  const startEdit = (row) => {
    if (!canEdit(row)) return;
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      rut: formatRut(row.rut || ''),
      email: row.email || '',
      phone: row.phone ? formatPhoneCL(row.phone) : '',
      password: '',
      role: row.role || 'programmer'
    });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', rut: '', email: '', phone: '', password: '', role: 'programmer' });
    setTouched({}); setDirty({}); setSubmitted(false); setServerErrs({});
    setFormModalOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTouched({ name: true, rut: true, email: true, phone: true, ...(editingId ? {} : { password: true }) });
    const errs = validate(form, !!editingId);
    if (Object.keys(errs).length) return;

    const payload = {
      name: form.name.trim(),
      rut: normalizeRut(form.rut),
      email: form.email.trim(),
      ...(form.phone ? { phone: normalizePhoneCL(form.phone) } : {}),
      role: form.role
    };

    try {
      setLoading(true);
      if (editingId) {
        await updateUserApi(editingId, payload, token);
        setBanner({ kind: 'info', msg: 'Usuario actualizado' });
      } else {
        // Guardar la contraseña temporal antes de crear el usuario
        const tempPassword = form.password.trim();
        const newUser = await createUserApi({ ...payload, password: tempPassword }, token);
        setBanner({ kind: 'info', msg: 'Usuario creado. Enviando email de bienvenida...' });

        // Intentar enviar email de bienvenida CON la contraseña temporal
        try {
          await sendWelcomeEmailApi(newUser.id, tempPassword, token);
          setBanner({ kind: 'info', msg: 'Usuario creado y email de bienvenida enviado correctamente' });
        } catch (emailError) {
          console.error('Error al enviar email:', emailError);
          setBanner({ kind: 'info', msg: 'Usuario creado, pero no se pudo enviar el email de bienvenida' });
        }
      }
      cancelEdit();         // cierra modal
      await refresh();
    } catch (err) {
      if (err.status === 409) {
        const msg = (err.message || err?.payload?.error || '').toLowerCase();
        const newErrs = {};
        if (msg.includes('email')) newErrs.email = 'Email ya registrado';
        if (msg.includes('rut')) newErrs.rut = 'RUT ya registrado';
        setServerErrs(s => ({ ...s, ...newErrs }));
        setTouched(s => ({ ...s, email: true, rut: true }));
      } else {
        setBanner({ kind: 'error', msg: err?.payload?.error || err?.message || 'Error al guardar' });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (row) => {
    if (!canDeactivate(row)) return; // bloqueo extra
    const next = row.status === 'active' ? 'inactive' : 'active';
    await handleApi(() => setUserStatusApi(row.id, next, token), next === 'active' ? 'Usuario activado' : 'Usuario desactivado');
  };

  const deleteSoft = async (row) => {
    if (!canDelete(row)) return; // bloqueo extra
    if (!window.confirm('¿Eliminar (soft)?')) return;
    await handleApi(() => setUserStatusApi(row.id, 'deleted', token), 'Usuario eliminado (soft)');
  };

  const openPassModal = (row) => setPassModal({ open: true, userId: row.id, userName: row.name, userEmail: row.email });
  const closePassModal = () => setPassModal({ open: false, userId: null, userName: '', userEmail: '' });
  const confirmPassModal = async (pw, pw2) => {
    if (!pw || pw.length < 8) return setBanner({ kind: 'error', msg: 'La contraseña debe tener al menos 8 caracteres.' });
    if (pw !== pw2) return setBanner({ kind: 'error', msg: 'Las contraseñas no coinciden.' });
    await handleApi(() => setUserPasswordApi(passModal.userId, pw, token), 'Contraseña actualizada');
    closePassModal();
  };

  return (
    <Layout title="Mejores Programadores" wide>
      <div className="container-tw">
        {/* Toolbar */}
        <div className="toolbar">
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Listado de Usuarios</h3>
          <div className="toolbar-actions">
            <button className="btn" onClick={openCreate} style={btnPrimary} title="Crear usuario">
              <FaPlus /><span>Nuevo Usuario</span>
            </button>
          </div>
        </div>

        <Banner kind={banner.kind} onClose={() => setBanner({ kind: 'info', msg: '' })}>
          {banner.msg}
        </Banner>

        {/* Filters Card */}
        <div className="filters-card">
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
            <input
              type="search"
              placeholder="Buscar nombre, email o RUT..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                flex: '1 1 280px',
                minWidth: '200px',
                fontSize: '14px'
              }}
            />
            {q && (
              <button className="btn" style={btnSecondary} onClick={() => setQ('')}>
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div>
          <UsersTable
            rows={paginatedData}
            canEdit={canEdit}
            canDeactivate={canDeactivate}
            canDelete={canDelete}
            onEdit={startEdit}
            onToggleStatus={toggleStatus}
            onDelete={deleteSoft}
            onChangePassword={openPassModal}
            page={page}
            pageSize={pageSize}
            totalCount={visibleRowsSorted.length}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* Modal Crear/Editar */}
      <UserFormModal
        open={formModalOpen}
        title={editingId ? `Editar usuario #${editingId}` : 'Crear usuario'}
        editingId={editingId}
        form={form}
        touched={touched}
        errors={errors}
        serverErrs={serverErrs}
        setField={setField}
        markTouched={markTouched}
        loading={loading}
        onClose={cancelEdit}
        onSubmit={submit}
      />

      {/* Modal Password */}
      <PasswordModal
        open={passModal.open}
        onClose={closePassModal}
        onConfirm={confirmPassModal}
        userName={passModal.userName}
        userEmail={passModal.userEmail}
      />
    </Layout>
  );
}
